import superagent from 'superagent';
import { getPhrase } from './data/phrases';
import { hidePendingRequest, showMessageBox, showPendingRequest, setPendingRequestMessage } from './actions/modal';

let registeredCalls = {};

export function registerWebapiCall(name, func) {
	registeredCalls[name] = func;
}

const call = {
	reg: async (name, params) => {
		const func = registeredCalls[name];
		if (!func) {
			throw 'registered webapi function "' + name + '" not found';
		}
		return func(params);
	},
	get: (url, params, headers) => {
		return new Promise((resolve, reject) => {
			superagent.get(url)
				.set(headers)
				.query(params)
				.then(result => {
					resolve(result.body ?? result.text);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	post: (url, params, headers) => {
		return new Promise((resolve, reject) => {
			superagent.post(url)
				.set(headers)
				.send(params)
				.then(result => {
					resolve(result.body ?? result.text);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	upload: (url, file, headers, progress) => {
		return new Promise((resolve, reject) => {
			superagent.post(url)
				.set(headers)
				.on('progress', (e) => {
					if (progress) {
						progress(e);
					}
				})
				.attach('file', file)
				.then(result => {
					resolve(result.body);
				})
				.catch(err => {
					reject(err);
				});
		});
	}
}

function parseUrl(url, defaultMethod) {
	let method = defaultMethod || 'get';
	if (url.startsWith('post:')) {
		method = 'post';
		url = url.substring(5);
	} else if (url.startsWith('get:')) {
		method = 'get';
		url = url.substring(4);
	} else if (url.startsWith('reg:')) {
		method = 'reg',
			url = url.substring(4);
	}
	return {
		method,
		url
	};
}

function identity(value) {
	return value;
}

class Webapi {
	#defaultHeaders;
	#decorateUrl;
	#defaultMethod;
	#processException;
	#listeners = [];

	#pendingRequestMessageMap = {};

	#mocks = {};


	addListener(lst) {
		this.#listeners.push(lst);
	}

	removeListener(lst) {
		this.#listeners = this.#listeners.filter(l => l !== lst);
	}

	setPendingRequestMessage(url, message) {
		this.#pendingRequestMessageMap[url] = message;
	}

	setDefaultMethod(value) {
		this.#defaultMethod = value;
		return this;
	}

	setDefaultHeaders(value) {
		this.#defaultHeaders = value;
		return this;
	}

	setDecorateUrl(value) {
		this.#decorateUrl = value
		return this;
	}

	setProcessException(value) {
		this.#processException = value;
		return this;
	}

	registerFunction(name, func) {
		func = func.bind(this);
		//console.log('refFunc', func);
		this[name] = func;
	}

	getDecoratedUrl(url) {
		return (this.#decorateUrl || identity)(url);
	}

	notifyListeners(url) {
		this.#listeners.forEach(lst => lst(url));
	}

	async upload(url, file, headers) {
		this.notifyListeners(url);
		const decorateUrl = this.#decorateUrl || identity;
		let prHandle = showPendingRequest(getPhrase('uploadingFile').replace('$fileName', file.name));
		if (this.#mocks.upload) {
			return await this.#mocks.upload({
				url: decorateUrl(url),
				method: 'post',
				file,
				headers: Object.assign({}, this.#defaultHeaders, headers || {})
			});
		}
		let progress = function (e) {
			setPendingRequestMessage(prHandle, getPhrase('uploadingFileProgress').replace('$fileName', file.name).replace('$progress', Math.round(e.percent)));
		};
		try {
			const res = await call.upload(decorateUrl(url), file, Object.assign({}, this.#defaultHeaders, headers || {}), progress);
			hidePendingRequest(prHandle);
			return res;
		} catch (ex) {
			hidePendingRequest(prHandle);
			if (this.#processException) {
				ex = this.#processException(ex, url);
			}
			if (typeof ex === 'string') {
				throw ex;
			} else {
				let msg = ex.customMessage;
				if (!msg) {
					msg = 'Při zpracování požadavku došlo k technické chybě: \n\n' + ex.message;
				}
				await showMessageBox(msg);
				return REPORTED_EXCEPTION;
			}
		}
	}

	async execute(url, params, headers, silent) {
		this.notifyListeners(url);
		params = params || {};
		headers = Object.assign({}, this.#defaultHeaders || {}, headers || {});
		const locator = parseUrl(url, this.#defaultMethod);
		const decorateUrl = this.#decorateUrl || identity;
		if (this.#mocks.execute) {
			return await this.#mocks.execute({
				url: locator.url,
				method: locator.method,
				params,
				headers
			});
		}
		let prHandle = null;
		try {
			prHandle = silent ? null : showPendingRequest(params._pendingRequestMessage || this.#pendingRequestMessageMap[url] || getPhrase('processingRequest'));
			const res = await call[locator.method](locator.method === 'reg' ? locator.url : decorateUrl(locator.url), params, headers);
			hidePendingRequest(prHandle);
			return res;
		} catch (ex) {
			hidePendingRequest(prHandle);
			if (this.#processException) {
				ex = this.#processException(ex, url);
			}
			if (typeof ex === 'string') {
				throw ex;
			} else {
				let msg = ex.customMessage;
				if (!msg) {
					msg = 'Při zpracování požadavku došlo k technické chybě: \n\n' + ex.message;
				}
				await showMessageBox(msg);
				return REPORTED_EXCEPTION;
			}
		}
	}

	mockExecute(callback) {
		if (typeof callback !== 'function') {
			throw 'Callback must be a function'
		}
		this.#mocks.execute = callback;
	}

	mockUpload(callback) {
		if (typeof callback !== 'function') {
			throw 'Callback must be a function'
		}
		this.#mocks.upload = callback;
	}

	unmock() {
		this.#mocks = {};
	}
}

const webapi = new Webapi();

export default webapi;

export const REPORTED_EXCEPTION = Infinity;
